/**
 * ⚖ F6 — "does a PERSON owe this reply?" — as ONE rule for the whole SPA.
 *
 * Since P7.3 the mission state `awaiting_user` is shared by two park kinds, and
 * the discriminator is the COUNTERPARTY, never the park kind: a
 * CONCIERGE-addressed handback parks identically to a specialist-addressed one,
 * but a HUMAN owes the first (the concierge is a human's proxy).
 *
 * ## Why this is a module and not two one-liners
 *
 * The platform makes the identical call in `mission-service/src/contract.ts`:
 *
 * ```ts
 * export function isHumanOwedCounterparty(to?: string): boolean {
 *   return (to ?? CONCIERGE_BUS_ALIAS) === CONCIERGE_BUS_ALIAS;
 * }
 * ```
 *
 * and its docblock names the exact drift this file exists to prevent: *"The
 * pairing of the `??` default WITH the alias comparison is the whole point of
 * the atom. Two sites each doing it by hand look locally correct while one
 * silently stops defaulting."* The SPA had ONE hand-rolled copy (the mission
 * timeline); R6 needed a second (the parked-ask notice). A third spelling of a
 * rule whose failure mode is *silent divergence* is how this lane's own
 * defect-of-the-week gets re-created, so both now call this.
 *
 * ⚠ **ABSENT means CONCIERGE-ADDRESSED, i.e. HUMAN-owed — never "unknown".**
 * The stored `to` is the leaf's RAW optional `outcome.to`, so absence is the
 * common shape rather than a corruption (`missions-harnesses` measured it on
 * real traffic, 2026-09-13). The SPA returned `null` here until R6-bis and that
 * was inverted in the reassuring direction; `mission-service`'s shipped
 * `humanOwedOf` resolves the identical fact the identical way, so any other
 * answer puts this surface in direct disagreement with the contract about one
 * park.
 *
 * ⚠ **A second human-proxy alias must be taught to BOTH sides.** The platform
 * hard-codes its single alias in `contract.ts`; this file hard-codes the
 * matching set. They are independent, so adding one HERE alone silently
 * re-creates the divergence. Chain: R6/R6-bis in
 * `ai-docs/unanswerable-park-chain-plan.md`.
 */

/** Bus aliases that stand for a HUMAN. Mirrors `CONCIERGE_BUS_ALIAS`. */
const HUMAN_PROXY_ALIASES = new Set(["concierge"]);

/**
 * `true` when a PERSON owes the reply to a handback park addressed to `to`.
 *
 * Mirrors `isHumanOwedCounterparty` in `mission-service/src/contract.ts`,
 * including its `??` default — see this module's docblock for why absence is
 * human-owed and not unknown.
 */
export function isHumanOwedCounterparty(to?: string): boolean {
  const alias = to?.trim().toLowerCase();
  if (!alias) return true; // absent ⇒ concierge-addressed ⇒ a human owes it
  return HUMAN_PROXY_ALIASES.has(alias);
}
